/**
 * The person the carrier calls at this end of the shipment.
 *
 * Starts from whatever the shipment already has, then, if the address matches
 * one in the address book, fills itself in from that location's contact — the
 * default one if it has several.
 */

import type { UUID } from "@/Shared/types";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { useEffect, useState } from "react";

interface BookShipmentAddressContactInput {
  context: LocationContext;
  shipment: PreBookingShipment;
  matchingSavedLocation: SavedLocation | undefined;
}

export function useBookShipmentAddressContact(
  input: BookShipmentAddressContactInput,
) {
  const { context, shipment, matchingSavedLocation } = input;

  const [contact, setContact] = useState<Contact>(
    (context === LocationContext.Pickup
      ? shipment.pickupContact
      : shipment.deliveryContact) || {
      contactName: "",
      phoneNumber: "",
      emailAddress: "",
      phoneNumberExtension: "",
    },
  );
  const [savedContactId, setSavedContactId] = useState<UUID>();

  function updateContact(contactUpdates: Contact) {
    setContact((prev) => ({
      ...prev,
      ...contactUpdates,
    }));
  }

  function findSavedContactToApply() {
    if (matchingSavedLocation) {
      if (matchingSavedLocation.contacts.length === 1) {
        return matchingSavedLocation.contacts[0];
      } else if (matchingSavedLocation.contacts.length > 1) {
        const defaultContact = matchingSavedLocation.contacts.find(
          (c) =>
            c.savedContactId === matchingSavedLocation.defaultSavedContactId,
        );

        if (defaultContact) {
          return defaultContact;
        } else {
          return matchingSavedLocation.contacts[0];
        }
      }
    }

    return undefined;
  }

  useEffect(function () {
    const c = findSavedContactToApply();

    if (c) {
      setSavedContactId(c.savedContactId);
      updateContact(c.contact);
    }
  }, []);

  function selectSavedContact(newSavedContactId: UUID) {
    const savedContact = matchingSavedLocation?.contacts.find(
      (sc) => sc.savedContactId === newSavedContactId,
    );

    if (!savedContact) {
      throw new Error("Contact not found");
    }
    updateContact(savedContact.contact);

    setSavedContactId(newSavedContactId);
  }

  return { contact, updateContact, savedContactId, selectSavedContact };
}
